<template>
	<loader v-if="!formReady"/>
	<form v-else class="wide sections" @submit.prevent="submit">
		<section class="section--floating section--full">
			<fieldset>
				<div>
					<div>
						<legend><span>Branding</span></legend>
					</div>
					<div>
						<label><span>Logo </span>
							<v-upload
								v-model="form.data.logo"
								accept="image/jpeg, image/png"
								idle="Tap to select file or drop files here..."
								:allow-multiple="false"/>
						</label>
						<label><span>Header background colour </span>
							<input type="color" v-model="form.data.appearance.header_background_color">
						</label>
						<label><span>Button colour </span>
							<input type="color" v-model="form.data.appearance.button_color">
						</label>
						<label><span>Button hover colour </span>
							<input type="color" v-model="form.data.appearance.button_hover_color">
						</label>
						<label><span>Highlight colour </span>
							<input type="color" v-model="form.data.appearance.highlight_color">
						</label>
						<label><span>Light highlight colour </span>
							<input type="color" v-model="form.data.appearance.light_highlight_color">
						</label>
						<label><span>Gradient start colour </span>
							<input type="color" v-model="form.data.appearance.gradient_start_color">
						</label>
						<label><span>Gradient end colour </span>
							<input type="color" v-model="form.data.appearance.gradient_end_color">
						</label>
					</div>
				</div>
			</fieldset>
			<fieldset>
				<div>
					<div>
						<legend><span>White Label Configuration</span></legend>
					</div>
					<div>
						<label><span>Domain </span>
							<input type="text" v-model="form.data.white_label.domain">
						</label>
						<label><span>Browser tab image URL </span>
							<input type="text" v-model="form.data.white_label.browser_tab_image_url">
						</label>
					</div>
				</div>
			</fieldset>
			<fieldset>
				<div>
					<div>
						<legend><span>Automation</span></legend>
						<div class="help-block">
							<p>These settings can be overridden for each lead type.</p>
						</div>
					</div>
					<div>
						<label><span>Default Maximum Assigned Lead Quota </span>
							<input type="text" v-model="form.data.automation.default_maximum_assigned_lead_quota">
						</label>
					</div>
				</div>
			</fieldset>
			<fieldset>
				<div>
					<div>
						<legend><span>Payment API Keys</span></legend>
						<div class="help-block">
							<p>These settings are required if you wish to take any type of payment for any type of lead.</p>
						</div>
					</div>
					<div>
						<label><span>Stripe publishable key </span>
							<input type="text" v-model="form.data.payments.stripe.publishable">
						</label>
						<label><span>Stripe secret key </span>
							<input type="text" v-model="form.data.payments.stripe.secret">
						</label>
					</div>
				</div>
			</fieldset>
			<section class="section section--expanded">
				<v-tabs>
					<v-tab
						class="sections" v-for="(service, serviceIndex) in form.options.services" :key="serviceIndex"
						:id="service.id" :label="service.names.plural">
						<section class="section">
							<header>
								<h2 class="beta" v-html="service.names.plural + ' Configuration'"></h2>
							</header>
							<fieldset>
								<div>
									<div>
										<legend><span
											v-html="'New ' + service.names.singular + ' Leads Automation'"></span>
										</legend>
									</div>
									<div>
										<label><span>Default maximum assigned lead quota </span>
											<input
												type="text"
												v-model="form.data.services[service.id].automation.leads.default_maximum_assigned_lead_quota">
											<div class="help-block">
												<p>This will override the
													<em>Automation > Default Maximum Assigned Lead Quota</em> setting specifically for {{ service.names.plural }}.
												</p>
											</div>
										</label>
										<label><span>Assign new leads to team </span>
											<div class="nothing-to-see-here" v-if="!form.options.teams.length">
												<div class="editorial">
													<p>You have no teams that new leads can be assigned to.</p>
													<router-link
														:to="{name: 'teams.create'}"
														v-if="$can('createTeams')">Create your first team</router-link>
												</div>
											</div>
											<select
												v-else
												v-model="form.data.services[service.id].automation.leads.team"
												@change="checkUser(service, 'leads')">
												<option :value="null" selected>—</option>
												<option
													v-for="(team, index) in form.options.teams"
													:key="index"
													:value="team.id"
													v-html="team.name"/>
											</select>
										</label>
										<label><span>Assign new leads to user </span>
											<select v-model="form.data.services[service.id].automation.leads.user">
												<option :value="null" selected>—</option>
												<option
													v-for="(user, index) in validUsers(service, 'leads')"
													:key="index"
													:value="user.id"
													v-html="$present('fullName', user.profile)"/>
											</select>
										</label>
									</div>
								</div>
							</fieldset>
							<fieldset v-if="'callCentre' !== $authed().type">
								<div>
									<div>
										<legend><span
											v-html="'New ' + service.names.singular + ' Instruction Automation'"></span>
										</legend>
									</div>
									<div>
										<label><span>Assign new instructions to team </span>
											<div class="nothing-to-see-here" v-if="!form.options.teams.length">
												<div class="editorial">
													<p>You have no teams that new instructions can be assigned to.</p>
													<router-link
														:to="{name: 'teams.create'}"
														v-if="$can('createTeams')">Create your first team</router-link>
												</div>
											</div>
											<select
												v-else
												v-model="form.data.services[service.id].automation.instructions.team"
												@change="checkUser(service, 'instructions')">
												<option :value="null" selected>—</option>
												<option
													v-for="(team, index) in form.options.teams"
													:key="index"
													:value="team.id"
													v-html="team.name"/>
											</select>
										</label>
										<label><span>Assign new instructions to user </span>
											<select
												v-model="form.data.services[service.id].automation.instructions.user">
												<option :value="null" selected>—</option>
												<option
													v-for="(user, index) in validUsers(service, 'instructions')"
													:key="index" :value="user.id"
													v-html="$present('fullName', user.profile)"/>
											</select>
										</label>
									</div>
								</div>
							</fieldset>
							<fieldset v-if="'callCentre' !== $authed().type">
								<div>
									<div>
										<legend><span v-html="service.names.plural + ' Requirements'"></span></legend>
									</div>
									<div>
										<label class="checkbox"><span>Fact find</span>
											<input
												type="checkbox"
												v-model="form.data.services[service.id].requirements.fact_find">
										</label>
										<label
											class="checkbox"
											v-if="form.data.payments.stripe.publishable && form.data.payments.stripe.secret"><span>Payment</span>
											<input
												type="checkbox"
												v-model="form.data.services[service.id].requirements.payment">
										</label>
										<label class="checkbox"><span>Appointment</span>
											<input
												type="checkbox"
												v-model="form.data.services[service.id].requirements.appointment">
										</label>
									</div>
								</div>
							</fieldset>
							<fieldset v-if="form.data.services[service.id].requirements.appointment">
								<div>
									<div>
										<legend><span v-html="service.names.plural + ' Appointments'"></span></legend>
									</div>
									<div>
										<label><span>Default appointment duration (minutes) </span>
											<input
												type="text"
												v-model="form.data.services[service.id].appointments.default_duration">
										</label>
									</div>
								</div>
							</fieldset>
							<fieldset
								v-if="form.data.services[service.id].requirements.payment && form.data.payments.stripe.publishable && form.data.payments.stripe.secret">
								<div>
									<div>
										<legend><span v-html="service.names.plural + ' Payments'"></span></legend>
									</div>
									<div>
										<label><span>Payment requirements </span>
											<div class="input-group">
												<label><span>Type </span>
													<select v-model="form.data.services[service.id].payments.type">
														<option value="" disabled selected>—</option>
														<option value="exact">Exact</option>
														<option value="minimum">Minimum</option>
														<option value="recommended">Recommended</option>
													</select>
												</label>
												<label><span>Amount </span>
													<input
														type="text"
														v-model="form.data.services[service.id].payments.amount">
												</label>
											</div>
										</label>
									</div>
								</div>
							</fieldset>
							<fieldset v-if="'callCentre' !== $authed().type">
								<div>
									<div>
										<legend><span v-html="service.names.plural + ' Description'"></span></legend>
									</div>
									<div>
										<label><span>Description</span>
											<textarea v-model="form.data.services[service.id].description"></textarea>
											<div class="help-block">
												<p>This description will be shown to potential introducers when they are introducing a new client.</p>
												<p>Introducers may be able to use this description to persuade a new client to consider your services.</p>
											</div>
										</label>
									</div>
								</div>
							</fieldset>
						</section>
					</v-tab>
				</v-tabs>
			</section>
			<div class="actions">
				<ul>
					<li>
						<process-button
							class="button button--primary" :processing="form.processing" text="Save Settings"/>
					</li>
				</ul>
			</div>
		</section>
	</form>
</template>
<script>
export default {
	data() {
		return {
			formReady: false,
			form: this.newForm({
				logo: null,
				appearance: {
					header_background_color: "",
					button_color: "",
					button_hover_color: "",
					highlight_color: "",
					light_highlight_color: "",
					gradient_start_color: "",
					gradient_end_color: "",
				},
				white_label: {
					domain: "",
					browser_tab_image_url: "",
				},
				automation: {
					default_maximum_assigned_lead_quota: "",
				},
				payments: {
					stripe: {
						publishable: "",
						secret: "",
					},
				},
				services: [
					{
						automation: {
							leads: {
								default_maximum_assigned_lead_quota: "",
								team: null,
								user: null,
							},
							instructions: {
								team: null,
								user: null,
							},
						},
						requirements: {
							fact_find: false,
							payment: false,
							appointment: false,
						},
						appointments: {
							default_duration: null,
						},
						payments: {
							type: "",
							amount: "",
						},
						description: "",
					}
				],
			}),
		};
	},

	computed: {
		sp() {
			return this.serviceProvider || this.$authed().service_provider;
		},
		checkedAutomationServices() {
			let self = this;
			return _.filter(self.form.options.services, function (service) {
				return _.has(self.form.data.automation.lead_assignment, service.id);
			});
		},
		stripeRequired() {
			return _.keys(this.paymentsForm.data.payments.services).length;
		},
		checkedPaymentServices() {
			let self = this;
			return _.filter(self.paymentsForm.options.services, function (service) {
				return _.has(self.paymentsForm.data.payments.services, service.id);
			});
		},
	},

	props: {
		serviceProvider: {
			type: Object,
			default() {
				return null;
			}
		}
	},

	methods: {
		checkUser(service, type) {
			let validUsers = this.validUsers(service, type);
			let validUser = _.find(validUsers, ["id", this.form.data.services[service.id].automation[type].user]);
			if (!validUser) {
				this.form.set("services." + service.id + ".automation." + type + ".user", null);
			}
		},
		validUsers(service, type) {
			return _.find(this.form.options.teams, ["id", this.form.data.services[service.id].automation[type].team])?.all_members || this.form.options.users;
		},
		async submit() {
			this.form.patch(
				this.route("service-providers/:serviceProvider/settings", this.sp)
			).then(data => {

			});
		}
	},

	created() {
		http().get("form-data/settings", {
			params: {
				serviceProvider: this.sp.id,
			}
		}).then(response => {
			let form = this.form;
			form.setOptions(response.data.options);
			form.prefill(response.data.settings);
			form.setKeys("services", _.values(_.mapValues(form.options.services, function (service) {
				return service.id;
			})));
			this.formReady = true;
		});
	},
};
</script>
